import React, { useState, useEffect, useMemo } from 'react';
import {
  Paper,
  Typography,
  Box,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Chip
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import SitesService from '../../../services/SitesService';
import { getSitesOrdersConfig, updateSitesOrdersConfig } from '../../../services/SettingsService';
import SnackbarUtils from '../../../utils/SnackbarUtils';
import { useSitesConfig } from '../SitesContext';
import { getLocations } from '../../../services/LocationService';

const parseDistanceRange = (name) => {
  const source = String(name || '');
  const match = source.match(/(\d+(?:[.,]\d+)?)\s*[-a]\s*(\d+(?:[.,]\d+)?)/i) || source.match(/(\d+(?:[.,]\d+)?)\D+(\d+(?:[.,]\d+)?)/);
  if (!match) return { minDistanceKm: '', maxDistanceKm: '' };
  return {
    minDistanceKm: String(match[1]).replace(',', '.'),
    maxDistanceKm: String(match[2]).replace(',', '.')
  };
};

const buildDistanceRateName = (minKm, maxKm) => `${minKm} - ${maxKm} km`;
const DELIVERY_INTEGRATION_CAN_BE_ENABLED = false;
const WEEK_DAYS = [
  { value: 1, label: 'Lunes' },
  { value: 2, label: 'Martes' },
  { value: 3, label: 'Miércoles' },
  { value: 4, label: 'Jueves' },
  { value: 5, label: 'Viernes' },
  { value: 6, label: 'Sábado' },
  { value: 7, label: 'Domingo' }
];

const emptyDaySchedule = () => ({
  shift1Start: '',
  shift1End: '',
  shift2Start: '',
  shift2End: ''
});

const normalizeDaySchedulesFromApi = (schedules = []) => {
  const base = WEEK_DAYS.reduce((acc, day) => {
    acc[day.value] = emptyDaySchedule();
    return acc;
  }, {});
  schedules.forEach((row) => {
    const day = Number(row?.dayOfWeek);
    const shift = Number(row?.shiftIndex);
    if (!base[day]) return;
    const start = row?.startHour || '';
    const end = row?.endHour || '';
    if (shift === 1) {
      base[day].shift1Start = start;
      base[day].shift1End = end;
    } else if (shift === 2) {
      base[day].shift2Start = start;
      base[day].shift2End = end;
    }
  });
  return base;
};

const EnviosTab = ({ leadingContent }) => {
  const { t } = useTranslation();
  const { config } = useSitesConfig();
  const [locations, setLocations] = useState([]);
  const [siteLocations, setSiteLocations] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(true);
  const [ecommerceLocationIds, setEcommerceLocationIds] = useState([]);

  const [shippingRatesLocationId, setShippingRatesLocationId] = useState(null);
  const [shippingRates, setShippingRates] = useState([]);
  const [shippingRatesLoading, setShippingRatesLoading] = useState(false);
  const [shippingRateDialogOpen, setShippingRateDialogOpen] = useState(false);
  const [shippingRateEditingId, setShippingRateEditingId] = useState(null);
  const [shippingRateForm, setShippingRateForm] = useState({
    minDistanceKm: '',
    maxDistanceKm: '',
    cost: '',
    tiers: []
  });
  const [deleteShippingRateDialogOpen, setDeleteShippingRateDialogOpen] = useState(false);
  const [rateToDelete, setRateToDelete] = useState(null);
  const [deletingRate, setDeletingRate] = useState(false);

  const [scheduleLocationId, setScheduleLocationId] = useState(null);
  const [effectiveEcommerceOpen, setEffectiveEcommerceOpen] = useState(null);
  const [pickupAddress, setPickupAddress] = useState('');
  const [pickupReference, setPickupReference] = useState('');
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [daySchedules, setDaySchedules] = useState(() => normalizeDaySchedulesFromApi([]));
  const [manualOverrideMode, setManualOverrideMode] = useState('auto'); // auto | forced_open | forced_closed
  const [manualOverrideUntil, setManualOverrideUntil] = useState('');
  const [saving, setSaving] = useState(false);
  const [deliveryIntegrationEnabled, setDeliveryIntegrationEnabled] = useState(true);
  const [deliveryIntegrationLoaded, setDeliveryIntegrationLoaded] = useState(false);

  useEffect(() => {
    const ids = config?.siteConfig?.ecommerceLocationIds;
    setEcommerceLocationIds(Array.isArray(ids) ? ids.map(Number) : []);
  }, [config]);

  useEffect(() => {
    const items = config?.siteConfig?.locations?.items;
    setSiteLocations(Array.isArray(items) ? items : []);
  }, [config]);

  const activeLocationIds = useMemo(
    () => new Set(locations.map((location) => Number(location.id))),
    [locations]
  );
  const activeEcommerceLocationIds = useMemo(
    () => ecommerceLocationIds.filter((id) => activeLocationIds.has(Number(id))),
    [activeLocationIds, ecommerceLocationIds]
  );

  useEffect(() => {
    let cancelled = false;
    const fetchLocations = async () => {
      try {
        setLoadingLocations(true);
        const locationsData = await getLocations();
        if (!cancelled) {
          setLocations(Array.isArray(locationsData) ? locationsData : []);
        }
      } catch (error) {
        if (!cancelled) {
          setLocations([]);
          console.error('Error loading locations in EnviosTab:', error);
        }
      } finally {
        if (!cancelled) {
          setLoadingLocations(false);
        }
      }
    };

    fetchLocations();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    getSitesOrdersConfig()
      .then((config) => {
        if (cancelled) return;
        setDeliveryIntegrationEnabled(config.deliveryIntegrationEnabled !== false);
      })
      .catch(() => {
        if (!cancelled) {
          setDeliveryIntegrationEnabled(true);
        }
      })
      .finally(() => {
        if (!cancelled) setDeliveryIntegrationLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const getLocationDisplayName = (id) => {
    const locationId = Number(id);
    const fromApi = locations.find((l) => Number(l.id) === locationId);
    if (fromApi?.name) return fromApi.name;

    const fromSiteById = siteLocations.find((l) => Number(l?.id) === locationId);
    if (fromSiteById?.name) return fromSiteById.name;

    return `Sucursal #${locationId}`;
  };

  useEffect(() => {
    if (activeEcommerceLocationIds.length > 0 && !shippingRatesLocationId) {
      setShippingRatesLocationId(activeEcommerceLocationIds[0]);
    }
    if (shippingRatesLocationId && !activeEcommerceLocationIds.includes(shippingRatesLocationId)) {
      setShippingRatesLocationId(activeEcommerceLocationIds[0] ?? null);
    }
  }, [activeEcommerceLocationIds, shippingRatesLocationId]);

  useEffect(() => {
    if (activeEcommerceLocationIds.length > 0 && !scheduleLocationId) {
      setScheduleLocationId(activeEcommerceLocationIds[0]);
    }
    if (scheduleLocationId && !activeEcommerceLocationIds.includes(scheduleLocationId)) {
      setScheduleLocationId(activeEcommerceLocationIds[0] ?? null);
    }
  }, [activeEcommerceLocationIds, scheduleLocationId]);

  useEffect(() => {
    if (!scheduleLocationId) {
      return;
    }
    let cancelled = false;
    setScheduleLoading(true);
    SitesService.getLocationSchedules(scheduleLocationId)
      .then((data) => {
        if (cancelled) return;
        setEffectiveEcommerceOpen(data?.isOpen === true);
        setPickupAddress(data?.pickupAddress || '');
        setPickupReference(data?.pickupReference || '');
        setDaySchedules(normalizeDaySchedulesFromApi(data?.schedules || []));
        if (data?.manualOverrideOpen === true) {
          setManualOverrideMode('forced_open');
        } else if (data?.manualOverrideOpen === false) {
          setManualOverrideMode('forced_closed');
        } else {
          setManualOverrideMode('auto');
        }
        setManualOverrideUntil(data?.manualOverrideUntil || '');
      })
      .catch(() => {
        if (!cancelled) {
          setPickupAddress('');
          setPickupReference('');
          setEffectiveEcommerceOpen(null);
          setDaySchedules(normalizeDaySchedulesFromApi([]));
          setManualOverrideMode('auto');
          setManualOverrideUntil('');
        }
      })
      .finally(() => {
        if (!cancelled) setScheduleLoading(false);
      });
    return () => { cancelled = true; };
  }, [scheduleLocationId]);

  useEffect(() => {
    if (!shippingRatesLocationId) {
      setShippingRates([]);
      return;
    }
    let cancelled = false;
    setShippingRatesLoading(true);
    SitesService.getShippingRates(shippingRatesLocationId)
      .then((data) => {
        if (!cancelled) setShippingRates(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled) {
          setShippingRates([]);
        }
      })
      .finally(() => { if (!cancelled) setShippingRatesLoading(false); });
    return () => { cancelled = true; };
  }, [shippingRatesLocationId]);

  const handleSaveSchedules = async () => {
    if (!scheduleLocationId) return;
    const builtSchedules = [];
    for (const day of WEEK_DAYS) {
      const row = daySchedules[day.value] || emptyDaySchedule();
      const shifts = [
        { index: 1, start: row.shift1Start, end: row.shift1End },
        { index: 2, start: row.shift2Start, end: row.shift2End }
      ];
      for (const shift of shifts) {
        const hasStart = Boolean(shift.start);
        const hasEnd = Boolean(shift.end);
        if (hasStart !== hasEnd) {
          SnackbarUtils.warning(`${day.label}: completá inicio y fin del turno ${shift.index}`);
          return;
        }
        if (!hasStart) continue;
        if (shift.start === shift.end) {
          SnackbarUtils.warning(`${day.label}: inicio y fin del turno ${shift.index} no pueden ser iguales`);
          return;
        }
        builtSchedules.push({
          dayOfWeek: day.value,
          shiftIndex: shift.index,
          startHour: shift.start,
          endHour: shift.end
        });
      }
    }

    if (manualOverrideMode !== 'auto' && !manualOverrideUntil) {
      SnackbarUtils.warning('Definí hasta cuándo aplica el cierre/apertura temporal');
      return;
    }

    const manualOverrideOpen =
      manualOverrideMode === 'forced_open'
        ? true
        : manualOverrideMode === 'forced_closed'
          ? false
          : null;

    await SitesService.updateLocationSchedules(
      scheduleLocationId,
      null,
      pickupAddress,
      pickupReference,
      builtSchedules,
      manualOverrideOpen,
      manualOverrideMode === 'auto' ? null : manualOverrideUntil,
      manualOverrideMode === 'auto'
    );
    const refreshed = await SitesService.getLocationSchedules(scheduleLocationId);
    setEffectiveEcommerceOpen(refreshed?.isOpen === true);
  };

  const handleSaveAll = async () => {
    try {
      setSaving(true);
      const ok = await updateSitesOrdersConfig({
        deliveryIntegrationEnabled: deliveryIntegrationEnabled && DELIVERY_INTEGRATION_CAN_BE_ENABLED
      });
      if (!ok) {
        throw new Error('No se pudo guardar la configuración de integración de envíos');
      }
      if (scheduleLocationId) {
        await handleSaveSchedules();
      }
      SnackbarUtils.success(t('saved') || 'Guardado');
    } catch (err) {
      SnackbarUtils.error(err?.response?.data?.message || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {leadingContent}

      <Paper sx={{ padding: 3, mt: 2 }}>
        <Typography variant="h6" gutterBottom>
          Integración con sistema de envíos Axium
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Si está habilitada, se usa todo el flujo de cadetería (dispatch, asignación y tracking). Si está deshabilitada, el envío queda en modo manual y solo podrás cambiar estado entre Pendiente y Entregado.
        </Typography>
        <FormControl size="small" sx={{ minWidth: 320 }}>
          <InputLabel>Estado de integración</InputLabel>
          <Select
            value={deliveryIntegrationEnabled && DELIVERY_INTEGRATION_CAN_BE_ENABLED ? 'true' : 'false'}
            label="Estado de integración"
            onChange={(e) => setDeliveryIntegrationEnabled(String(e.target.value) === 'true')}
            disabled={!deliveryIntegrationLoaded || saving || !DELIVERY_INTEGRATION_CAN_BE_ENABLED}
          >
            <MenuItem value="true" disabled={!DELIVERY_INTEGRATION_CAN_BE_ENABLED}>
              Habilitada (flujo completo)
            </MenuItem>
            <MenuItem value="false">Deshabilitada (modo manual)</MenuItem>
          </Select>
        </FormControl>
      </Paper>

      {/* 1. Tarifas de envío */}
      <Paper sx={{ padding: 3, mt: 2 }}>
        <Typography variant="h6" gutterBottom>
          {t('shipping_rates') || 'Tarifas de envío'}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('shipping_rates_description') ||
            'Configura tarifas por sucursal y rango de distancia (km). El costo y envío gratis (mínimo) se aplican en el checkout.'}
        </Typography>
        {activeEcommerceLocationIds.length === 0 ? (
          <Typography color="text.secondary">
            {t('configure_locations_first') || 'Configura las sucursales en la sección Tienda para definir tarifas por sucursal.'}
          </Typography>
        ) : (
          <>
            <FormControl size="small" sx={{ minWidth: 220, mb: 2 }}>
              <InputLabel>{t('branch') || 'Sucursal'}</InputLabel>
              <Select
                value={shippingRatesLocationId ?? ''}
                label={t('branch') || 'Sucursal'}
                  onChange={(e) => setShippingRatesLocationId(e.target.value ? Number(e.target.value) : null)}
                disabled={shippingRatesLoading || loadingLocations}
              >
                {activeEcommerceLocationIds.map((id) => {
                  return (
                    <MenuItem key={id} value={id}>
                      {getLocationDisplayName(id)}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
            {shippingRatesLoading ? (
              <Typography color="text.secondary">{t('loading') || 'Cargando...'}</Typography>
            ) : (
              <>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('distance_range_km') || 'Rango (km)'}</TableCell>
                      <TableCell align="right">{t('cost') || 'Costo'}</TableCell>
                      <TableCell>{t('shipping_tiers') || 'Escalones'}</TableCell>
                      <TableCell align="right">{t('actions') || 'Acciones'}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {shippingRates.map((r) => {
                      const parsedRange = parseDistanceRange(r.name);
                      const minKm = r.minKm != null ? String(r.minKm) : parsedRange.minDistanceKm;
                      const maxKm = r.maxKm != null ? String(r.maxKm) : parsedRange.maxDistanceKm;
                      const rangeLabel = minKm && maxKm ? `${minKm} - ${maxKm} km` : '—';
                      return (
                      <TableRow key={r.id}>
                        <TableCell>{rangeLabel}</TableCell>
                        <TableCell align="right">{r.cost != null ? Number(r.cost).toFixed(2) : '-'}</TableCell>
                        <TableCell>
                          {Array.isArray(r.tiers) && r.tiers.length > 0
                            ? r.tiers
                                .map((tier) =>
                                  Number(tier.discountPercent) >= 100
                                    ? `≥${Number(tier.minCartTotal).toFixed(0)}: ${t('free_shipping') || 'Envío gratis'}`
                                    : `≥${Number(tier.minCartTotal).toFixed(0)}: ${Number(tier.discountPercent).toFixed(0)}%`
                                )
                                .join(' · ')
                            : '—'}
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setShippingRateEditingId(r.id);
                              SitesService.getShippingRateById(r.id)
                                .then((detail) => {
                                  const tiersFromApi = Array.isArray(detail.tiers) && detail.tiers.length > 0
                                    ? detail.tiers.map((t) => ({
                                        minCartTotal: String(t.minCartTotal ?? ''),
                                        discountPercent: String(t.discountPercent ?? '')
                                      }))
                                    : detail.freeShippingMin != null
                                      ? [{ minCartTotal: String(detail.freeShippingMin), discountPercent: '100' }]
                                      : [];
                                  setShippingRateForm({
                                    minDistanceKm: String(detail.minKm ?? parseDistanceRange(detail.name).minDistanceKm ?? ''),
                                    maxDistanceKm: String(detail.maxKm ?? parseDistanceRange(detail.name).maxDistanceKm ?? ''),
                                    cost: detail.cost != null ? String(detail.cost) : '',
                                    tiers: tiersFromApi
                                  });
                                  setShippingRateDialogOpen(true);
                                })
                                .catch(() => SnackbarUtils.error('Error al cargar tarifa'));
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => {
                              setRateToDelete({ id: r.id, name: rangeLabel });
                              setDeleteShippingRateDialogOpen(true);
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    )})}
                  </TableBody>
                </Table>
                {shippingRates.length === 0 && (
                  <Typography color="text.secondary" sx={{ py: 2 }}>
                    {t('no_shipping_rates') || 'No hay tarifas. Agrega una para esta sucursal.'}
                  </Typography>
                )}
                <Button
                  startIcon={<AddIcon />}
                  variant="outlined"
                  size="small"
                  sx={{ mt: 2 }}
                  onClick={() => {
                    setShippingRateEditingId(null);
                    setShippingRateForm({
                      minDistanceKm: '',
                      maxDistanceKm: '',
                      cost: '',
                      tiers: []
                    });
                    setShippingRateDialogOpen(true);
                  }}
                >
                  {t('add_shipping_rate') || 'Agregar tarifa'}
                </Button>
              </>
            )}
          </>
        )}
      </Paper>

      {/* Dialog tarifa */}
      <Dialog open={shippingRateDialogOpen} onClose={() => setShippingRateDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {shippingRateEditingId ? t('edit_shipping_rate') || 'Editar tarifa' : t('add_shipping_rate') || 'Agregar tarifa'}
        </DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label={t('min_distance_km') || 'Distancia mínima (km)'}
            type="number"
            inputProps={{ step: 0.1, min: 0 }}
            value={shippingRateForm.minDistanceKm}
            onChange={(e) => setShippingRateForm((f) => ({ ...f, minDistanceKm: e.target.value }))}
            sx={{ mr: 1, width: '49%' }}
          />
          <TextField
            margin="dense"
            label={t('max_distance_km') || 'Distancia máxima (km)'}
            type="number"
            inputProps={{ step: 0.1, min: 0.1 }}
            value={shippingRateForm.maxDistanceKm}
            onChange={(e) => setShippingRateForm((f) => ({ ...f, maxDistanceKm: e.target.value }))}
            sx={{ width: '49%' }}
          />
          <TextField
            margin="dense"
            label={t('cost') || 'Costo'}
            type="number"
            inputProps={{ step: 0.01, min: 0 }}
            fullWidth
            value={shippingRateForm.cost}
            onChange={(e) => setShippingRateForm((f) => ({ ...f, cost: e.target.value }))}
          />
          <Box sx={{ mt: 1.5, mb: 0.5 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              {t('shipping_tiers') || 'Escalones de descuento'}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              {t('shipping_tiers_help') ||
                'Si el carrito supera el monto, se aplica ese % de descuento al costo. 100% = envío gratis. Ordenados por monto (menor a mayor).'}
            </Typography>
            {(shippingRateForm.tiers || []).map((tier, idx) => (
              <Box key={idx} sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
                <TextField
                  size="small"
                  type="number"
                  label={t('min_cart_total') || 'Mín. carrito'}
                  inputProps={{ step: 0.01, min: 0 }}
                  value={tier.minCartTotal}
                  onChange={(e) =>
                    setShippingRateForm((f) => ({
                      ...f,
                      tiers: f.tiers.map((t, i) => (i === idx ? { ...t, minCartTotal: e.target.value } : t))
                    }))
                  }
                  sx={{ flex: 1 }}
                />
                <TextField
                  size="small"
                  type="number"
                  label={t('discount_percent') || '% descuento'}
                  inputProps={{ step: 1, min: 0, max: 100 }}
                  value={tier.discountPercent}
                  onChange={(e) =>
                    setShippingRateForm((f) => ({
                      ...f,
                      tiers: f.tiers.map((t, i) => (i === idx ? { ...t, discountPercent: e.target.value } : t))
                    }))
                  }
                  sx={{ width: 120 }}
                />
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => setShippingRateForm((f) => ({ ...f, tiers: f.tiers.filter((_, i) => i !== idx) }))}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() =>
                setShippingRateForm((f) => ({
                  ...f,
                  tiers: [...(f.tiers || []), { minCartTotal: '', discountPercent: '' }]
                }))
              }
            >
              {t('add_tier') || 'Agregar escalón'}
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShippingRateDialogOpen(false)}>{t('cancel') || 'Cancelar'}</Button>
          <Button
            variant="contained"
            onClick={() => {
              const minKm = parseFloat(String(shippingRateForm.minDistanceKm).replace(',', '.'));
              const maxKm = parseFloat(String(shippingRateForm.maxDistanceKm).replace(',', '.'));
              const cost = parseFloat(shippingRateForm.cost);
              if (Number.isNaN(minKm) || Number.isNaN(maxKm) || minKm < 0 || maxKm <= minKm) {
                SnackbarUtils.warning(t('fill_distance_range_valid') || 'Rango de distancia inválido');
                return;
              }
              if (Number.isNaN(cost) || cost < 0) {
                SnackbarUtils.warning(t('fill_name_and_cost') || 'Costo requerido');
                return;
              }
              const name = buildDistanceRateName(minKm, maxKm);
              const tiers = (Array.isArray(shippingRateForm.tiers) ? shippingRateForm.tiers : [])
                .map((t) => ({
                  minCartTotal: parseFloat(String(t.minCartTotal).replace(',', '.')) || 0,
                  discountPercent: Math.min(100, Math.max(0, parseFloat(String(t.discountPercent).replace(',', '.')) || 0))
                }))
                .sort((a, b) => a.minCartTotal - b.minCartTotal);
              const body = {
                name,
                cost,
                minKm,
                maxKm,
                tiers
              };
              const selectedLocationId = Number(shippingRatesLocationId);
              const locationExists = locations.some((l) => Number(l.id) === selectedLocationId);
              if (!locationExists) {
                SnackbarUtils.error('La sucursal seleccionada no existe. Revisá la configuración de sucursales.');
                return;
              }
              if (shippingRateEditingId) {
                SitesService.updateShippingRate(shippingRateEditingId, body)
                  .then(() => {
                    setShippingRateDialogOpen(false);
                    return SitesService.getShippingRates(shippingRatesLocationId);
                  })
                  .then((data) => setShippingRates(Array.isArray(data) ? data : []))
                  .then(() => SnackbarUtils.success(t('shipping_rate_saved') || 'Tarifa guardada'))
                  .catch((err) =>
                    SnackbarUtils.error(
                      err?.response?.data?.error_message ||
                      err?.response?.data?.message ||
                      err?.response?.data?.error ||
                      'Error al guardar'
                    )
                  );
              } else {
                SitesService.createShippingRate({ locationId: shippingRatesLocationId, ...body })
                  .then(() => {
                    setShippingRateDialogOpen(false);
                    return SitesService.getShippingRates(shippingRatesLocationId);
                  })
                  .then((data) => setShippingRates(Array.isArray(data) ? data : []))
                  .then(() => SnackbarUtils.success(t('shipping_rate_saved') || 'Tarifa guardada'))
                  .catch((err) =>
                    SnackbarUtils.error(
                      err?.response?.data?.error_message ||
                      err?.response?.data?.message ||
                      err?.response?.data?.error ||
                      'Error al crear'
                    )
                  );
              }
            }}
          >
            {t('save') || 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteShippingRateDialogOpen}
        onClose={() => !deletingRate && setDeleteShippingRateDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t('confirm_delete') || 'Confirmar eliminación'}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('confirm_delete_shipping_rate_message', { name: rateToDelete?.name }) ||
              `¿Eliminar la tarifa "${rateToDelete?.name}"? Esta acción no se puede deshacer.`}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button onClick={() => setDeleteShippingRateDialogOpen(false)} disabled={deletingRate}>
            {t('cancel') || 'Cancelar'}
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={deletingRate}
            onClick={() => {
              if (!rateToDelete) return;
              setDeletingRate(true);
              SitesService.deleteShippingRate(rateToDelete.id)
                .then(() => {
                  setShippingRates((prev) => prev.filter((x) => x.id !== rateToDelete.id));
                  setDeleteShippingRateDialogOpen(false);
                  setRateToDelete(null);
                  SnackbarUtils.success(t('shipping_rate_deleted') || 'Tarifa eliminada');
                })
                .catch((err) =>
                  SnackbarUtils.error(
                    err.response?.data?.error_message || err.response?.data?.message || t('error_deleting') || 'Error al eliminar'
                  )
                )
                .finally(() => setDeletingRate(false));
            }}
          >
            {deletingRate ? t('deleting') || 'Eliminando...' : t('delete') || 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 2. Estado de envíos por sucursal */}
      <Paper sx={{ padding: 3, mt: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, mb: 0.5 }}>
          <Typography variant="h6">
            {'Estado de envíos por sucursal'}
          </Typography>
          <Chip
            size="small"
            label={
              effectiveEcommerceOpen == null
                ? 'Verificando estado...'
                : effectiveEcommerceOpen
                  ? 'Ecommerce abierto'
                  : 'Ecommerce cerrado'
            }
            color={
              effectiveEcommerceOpen == null
                ? 'default'
                : effectiveEcommerceOpen
                  ? 'success'
                  : 'error'
            }
            variant={effectiveEcommerceOpen == null ? 'outlined' : 'filled'}
          />
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {'El estado de envíos se define por los horarios y el override temporal manual.'}
        </Typography>
        {activeEcommerceLocationIds.length === 0 ? (
          <Typography color="text.secondary">
            {t('configure_locations_first') || 'Configura las sucursales en la sección Tienda para definir envíos por sucursal.'}
          </Typography>
        ) : (
          <>
            <FormControl size="small" sx={{ minWidth: 220, mb: 2 }}>
              <InputLabel>{t('branch') || 'Sucursal'}</InputLabel>
              <Select
                value={scheduleLocationId ?? ''}
                label={t('branch') || 'Sucursal'}
                onChange={(e) => setScheduleLocationId(e.target.value ? Number(e.target.value) : null)}
                disabled={scheduleLoading || loadingLocations}
              >
                {activeEcommerceLocationIds.map((id) => {
                  return (
                    <MenuItem key={id} value={id}>
                      {getLocationDisplayName(id)}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
            {scheduleLoading ? (
              <Typography color="text.secondary" sx={{ py: 2 }}>
                {t('loading') || 'Cargando...'}
              </Typography>
            ) : (
              <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  label="Dirección para retiro"
                  value={pickupAddress}
                  onChange={(e) => setPickupAddress(e.target.value)}
                  fullWidth
                  placeholder="Ej: Av. Italia 1234, Montevideo"
                />
                <TextField
                  label="Referencia para retiro (opcional)"
                  value={pickupReference}
                  onChange={(e) => setPickupReference(e.target.value)}
                  fullWidth
                  multiline
                  minRows={2}
                  placeholder="Ej: Entrar por puerta lateral, mostrar número de pedido"
                />

                <Box sx={{ mt: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                    Horarios de envío por día
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Podés configurar hasta 2 turnos por día. Si el cierre es en madrugada, cargá la hora de fin menor que la de inicio (ej: 21:00 a 02:00).
                  </Typography>
                  <Grid container spacing={1.5}>
                    {WEEK_DAYS.map((day) => {
                      const row = daySchedules[day.value] || emptyDaySchedule();
                      return (
                        <Grid item xs={12} key={day.value}>
                          <Box
                            sx={{
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: 2,
                              p: 1.5,
                              display: 'flex',
                              flexDirection: { xs: 'column', md: 'row' },
                              gap: 1.5,
                              alignItems: { xs: 'stretch', md: 'center' }
                            }}
                          >
                            <Typography sx={{ width: { xs: '100%', md: 120 }, fontWeight: 600 }}>
                              {day.label}
                            </Typography>
                            <TextField
                              size="small"
                              type="time"
                              label="Turno 1 inicio"
                              InputLabelProps={{ shrink: true }}
                              value={row.shift1Start}
                              onChange={(e) =>
                                setDaySchedules((prev) => ({
                                  ...prev,
                                  [day.value]: { ...row, shift1Start: e.target.value }
                                }))
                              }
                            />
                            <TextField
                              size="small"
                              type="time"
                              label="Turno 1 fin"
                              InputLabelProps={{ shrink: true }}
                              value={row.shift1End}
                              onChange={(e) =>
                                setDaySchedules((prev) => ({
                                  ...prev,
                                  [day.value]: { ...row, shift1End: e.target.value }
                                }))
                              }
                            />
                            <TextField
                              size="small"
                              type="time"
                              label="Turno 2 inicio"
                              InputLabelProps={{ shrink: true }}
                              value={row.shift2Start}
                              onChange={(e) =>
                                setDaySchedules((prev) => ({
                                  ...prev,
                                  [day.value]: { ...row, shift2Start: e.target.value }
                                }))
                              }
                            />
                            <TextField
                              size="small"
                              type="time"
                              label="Turno 2 fin"
                              InputLabelProps={{ shrink: true }}
                              value={row.shift2End}
                              onChange={(e) =>
                                setDaySchedules((prev) => ({
                                  ...prev,
                                  [day.value]: { ...row, shift2End: e.target.value }
                                }))
                              }
                            />
                          </Box>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Box>

                <Box sx={{ mt: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                    Cierre temporal manual
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    El modo automático usa el horario configurado. Podés forzar abierto/cerrado temporalmente.
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 1.5 }}>
                    <FormControl size="small" sx={{ minWidth: 260 }}>
                      <InputLabel>Modo</InputLabel>
                      <Select
                        value={manualOverrideMode}
                        label="Modo"
                        onChange={(e) => setManualOverrideMode(e.target.value)}
                      >
                        <MenuItem value="auto">Automático (por horario)</MenuItem>
                        <MenuItem value="forced_closed">Cerrar temporalmente</MenuItem>
                        <MenuItem value="forced_open">Abrir temporalmente</MenuItem>
                      </Select>
                    </FormControl>
                    <TextField
                      size="small"
                      type="datetime-local"
                      label="Hasta"
                      InputLabelProps={{ shrink: true }}
                      value={manualOverrideUntil}
                      onChange={(e) => setManualOverrideUntil(e.target.value)}
                      disabled={manualOverrideMode === 'auto'}
                      helperText={manualOverrideMode === 'auto' ? 'Sin override manual' : 'Fecha/hora fin del override'}
                      sx={{ minWidth: 280 }}
                    />
                  </Box>
                </Box>
              </Box>
            )}
          </>
        )}
      </Paper>

      {/* Un solo Guardar por tab (abajo a la derecha, mismo patrón que EcommerceTab) */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
        <Button variant="contained" color="primary" onClick={handleSaveAll} disabled={saving}>
          {saving ? (t('saving') || 'Guardando...') : (t('save') || 'Guardar')}
        </Button>
      </Box>
    </>
  );
};

export default EnviosTab;
